import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import { Wrapper, StyledTextfield, StyledButton } from './styled';
import { useState, FC, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import InputLabel from '@mui/material/InputLabel';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import FormHelperText from '@mui/material/FormHelperText';
import Snackbar from '@mui/material/Snackbar';

const getRoles = process.env.REACT_APP_API_URL + 'api/Account/GetRoles';
const postCreateUser = process.env.REACT_APP_API_URL + 'api/Account/CreateUser';
const postUpdateUser = process.env.REACT_APP_API_URL + 'api/Account/UpdateUser';

const REQUIRED_TEXT = 'Обязательно для заполнения';

const Create: FC<{
    handleBack: () => void;
    title?: string;
    selectedUserData?: {
        id: string;
        name: string;
        surname: string;
        patronymic: string;
        organizationName: number;
        organizationId: number;
        roles: string[];
    };
}> = ({ handleBack, title = 'Создать нового пользователя', selectedUserData }) => {
    const [userName, setUserName] = useState('');
    const [organizationId, setOrganizationId] = useState(selectedUserData?.organizationId || 0);
    const [name, setName] = useState(selectedUserData?.name || '');
    const [surname, setSurname] = useState(selectedUserData?.surname || '');
    const [patronymic, setPatronymic] = useState(selectedUserData?.patronymic || '');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [roles, setRoles] = useState<{ description: string; systemName: string }[] | undefined>(undefined);
    const [selectedRole, setSelectedRole] = useState<string[]>(selectedUserData?.roles || []);
    const [isFormHasError, setIsFormHasError] = useState(false);
    const [isLoadingCreateUser, setIsLoadingCreateUser] = useState(false);
    const [isSuccessCreate, setIsSuccessCreate] = useState(false);
    const [isSuccessUpdate, setIsSuccessUpdate] = useState(false);

    const navigate = useNavigate();

    const create = async () => {
        const isFormHasError =
            userName.length === 0 ||
            organizationId === 0 ||
            name.length === 0 ||
            surname.length === 0 ||
            patronymic.length === 0 ||
            password.length === 0 ||
            selectedRole.length === 0;

        if (isFormHasError) {
            setIsFormHasError(true);
        } else {
            setIsFormHasError(false);
            setIsLoadingCreateUser(true);

            await axios
                .post(
                    postCreateUser,
                    {
                        userName: userName,
                        organizationId: organizationId,
                        name: name,
                        surname: surname,
                        patronymic: patronymic,
                        password: password,
                        roles: selectedRole,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                        },
                    },
                )
                .then((data) => {
                    if (data.status === 200) {
                        setUserName('');
                        setOrganizationId(0);
                        setName('');
                        setSurname('');
                        setPatronymic('');
                        setPassword('');
                        setSelectedRole([]);
                        setIsSuccessCreate(true);
                    }
                })
                .catch((err) => alert(`не удалось создать пользователя потому, что ${err.response.data.detail}`))
                .finally(() => setIsLoadingCreateUser(false));
        }
    };

    const update = async () => {
        const isFormHasError =
            userName.length === 0 ||
            organizationId === 0 ||
            name.length === 0 ||
            surname.length === 0 ||
            patronymic.length === 0 ||
            password.length === 0 ||
            selectedRole.length === 0;

        if (isFormHasError) {
            setIsFormHasError(true);
        } else {
            setIsFormHasError(false);
            setIsLoadingCreateUser(true);

            await axios
                .post(
                    postUpdateUser,
                    {
                        id: selectedUserData?.id,
                        organizationId: organizationId,
                        name: name,
                        surname: surname,
                        patronymic: patronymic,
                        password: password,
                        roles: selectedRole,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                        },
                    },
                )
                .then((data) => {
                    if (data.status === 200) {
                        setUserName('');
                        setOrganizationId(0);
                        setName('');
                        setSurname('');
                        setPatronymic('');
                        setPassword('');
                        setSelectedRole([]);
                        setIsSuccessUpdate(true);
                    }
                })
                .catch((err) => alert(`не удалось обновить пользователя потому, что ${err.response.data.detail}`))
                .finally(() => setIsLoadingCreateUser(false));
        }
    };

    useEffect(() => {
        setIsLoading(true);

        axios
            .get(getRoles, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            })
            .then((data) => {
                setRoles(data.data);
            })
            .catch((err) => {
                if (err.response.status === 401) {
                    localStorage.clear();
                    navigate('/login');
                } else {
                    alert('Не удалось загрузить список ролей, ' + err);
                }
            })
            .finally(() => setIsLoading(false));
    }, []);

    return (
        <Wrapper>
            {isLoading ? (
                <CircularProgress />
            ) : (
                <>
                    <Box display='flex' width='100%' justifyContent='space-evenly' alignItems='center'>
                        <IconButton color='primary' onClick={handleBack}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography width='50%'>{title}</Typography>
                    </Box>

                    <StyledTextfield
                        label='Логин'
                        placeholder='Введите логин'
                        variant='outlined'
                        onChange={(event) => setUserName(event.target.value)}
                        required
                        error={isFormHasError && userName.length === 0}
                        helperText={isFormHasError && userName.length === 0 && REQUIRED_TEXT}
                        size='small'
                        disabled={isLoadingCreateUser}
                        value={userName}
                    />
                    <StyledTextfield
                        label='Пароль'
                        placeholder='Введите пароль'
                        variant='outlined'
                        onChange={(event) => setPassword(event.target.value)}
                        required
                        error={isFormHasError && password.length === 0}
                        helperText={isFormHasError && password.length === 0 && REQUIRED_TEXT}
                        size='small'
                        disabled={isLoadingCreateUser}
                        value={password}
                    />
                    <StyledTextfield
                        label='Медицинская организация'
                        placeholder='Введите медицинскую организацию'
                        variant='outlined'
                        onChange={(event) => setOrganizationId(Number(event.target.value))}
                        type='number'
                        required
                        error={isFormHasError && organizationId === 0}
                        helperText={isFormHasError && organizationId === 0 && REQUIRED_TEXT}
                        size='small'
                        disabled={isLoadingCreateUser}
                        value={organizationId === 0 ? '' : organizationId}
                    />
                    <FormControl
                        fullWidth
                        size='small'
                        error={isFormHasError && selectedRole.length === 0}
                        disabled={isLoadingCreateUser}
                    >
                        <InputLabel>Должность</InputLabel>
                        <Select
                            multiple
                            required
                            value={selectedRole as string[]}
                            onChange={(event) => {
                                const {
                                    target: { value },
                                } = event;
                                setSelectedRole(typeof value === 'string' ? value.split(',') : value);
                            }}
                            input={<OutlinedInput label='Должность' />}
                            renderValue={(selected) => {
                                const selectedNames = selected.reduce((acc: string[], select) => {
                                    let selectName = '';
                                    roles?.map(({ description, systemName }) => {
                                        if (systemName === select) {
                                            selectName = description;
                                        }
                                    });

                                    acc.push(selectName);

                                    return acc;
                                }, []);

                                return selectedNames.join(', ');
                            }}
                            error={isFormHasError && selectedRole.length === 0}
                        >
                            {roles?.map(({ description, systemName }) => (
                                <MenuItem key={systemName} value={systemName}>
                                    <Checkbox checked={selectedRole.includes(systemName)} />
                                    <ListItemText primary={description} />
                                </MenuItem>
                            ))}
                        </Select>
                        {isFormHasError && selectedRole.length === 0 && (
                            <FormHelperText>{REQUIRED_TEXT}</FormHelperText>
                        )}
                    </FormControl>
                    <StyledTextfield
                        label='Фамилия пользователя'
                        placeholder='Введите фамилию пользователя'
                        variant='outlined'
                        onChange={(event) => setSurname(event.target.value)}
                        required
                        error={isFormHasError && surname.length === 0}
                        helperText={isFormHasError && surname.length === 0 && REQUIRED_TEXT}
                        size='small'
                        disabled={isLoadingCreateUser}
                        value={surname}
                    />
                    <StyledTextfield
                        label='Имя пользователя'
                        placeholder='Введите имя пользователя'
                        variant='outlined'
                        onChange={(event) => setName(event.target.value)}
                        required
                        error={isFormHasError && name.length === 0}
                        helperText={isFormHasError && name.length === 0 && REQUIRED_TEXT}
                        size='small'
                        disabled={isLoadingCreateUser}
                        value={name}
                    />
                    <StyledTextfield
                        label='Отчество пользователя'
                        placeholder='Введите отчество пользователя'
                        variant='outlined'
                        onChange={(event) => setPatronymic(event.target.value)}
                        required
                        error={isFormHasError && patronymic.length === 0}
                        helperText={isFormHasError && patronymic.length === 0 && REQUIRED_TEXT}
                        size='small'
                        disabled={isLoadingCreateUser}
                        value={patronymic}
                    />
                    {isLoadingCreateUser ? (
                        <CircularProgress />
                    ) : (
                        <StyledButton
                            variant='outlined'
                            size='large'
                            type='submit'
                            onClick={selectedUserData ? update : create}
                            disabled={isLoadingCreateUser}
                        >
                            {selectedUserData ? 'Редактировать' : 'Создать'}
                        </StyledButton>
                    )}
                </>
            )}
            <Snackbar open={isSuccessCreate} autoHideDuration={2000} onClose={() => setIsSuccessCreate(false)}>
                <Alert
                    onClose={() => setIsSuccessCreate(false)}
                    severity='success'
                    variant='filled'
                    sx={{ width: '100%' }}
                >
                    Пользователь успешно создан
                </Alert>
            </Snackbar>
            <Snackbar open={isSuccessUpdate} autoHideDuration={2000} onClose={() => setIsSuccessUpdate(false)}>
                <Alert
                    onClose={() => setIsSuccessUpdate(false)}
                    severity='success'
                    variant='filled'
                    sx={{ width: '100%' }}
                >
                    Пользователь успешно обновлен
                </Alert>
            </Snackbar>
        </Wrapper>
    );
};

export default Create;
